import { useBrandLocale } from '../../hooks/useBrandLocale';
import { AddressInput } from '../../components/addressInput';
import { Field, FormGrid, Input, Muted, Stack } from '../../components/ui';
import { addressFromPlace, type StayAddress } from './stayAddress';

export * from './stayAddress';

/**
 * The address step: search with Google Places for the point on the map, then
 * check the street, unit, city, state and ZIP code the search filled in.
 */
export function StayAddressForm({
  value,
  onChange,
  error,
}: {
  value: StayAddress;
  onChange: (value: StayAddress) => void;
  error?: string | null;
}) {
  // Spec 028: Canadian brands say province and postal code.
  const canada = useBrandLocale().country === 'CA';
  const located = value.latitude !== null && value.longitude !== null;
  return (
    <Stack $gap={6}>
      <AddressInput
        value=""
        placeholder="Search for your address"
        onLocationSelect={place => onChange(addressFromPlace(place, value))}
      />
      <FormGrid $cols={2}>
        <Field label="Street address" htmlFor="stay-street" required>
          <Input
            id="stay-street"
            value={value.street}
            onChange={event => onChange({ ...value, street: event.target.value })}
          />
        </Field>
        <Field label="Apt, suite or unit (optional)" htmlFor="stay-unit">
          <Input
            id="stay-unit"
            value={value.unit}
            onChange={event => onChange({ ...value, unit: event.target.value })}
          />
        </Field>
        <Field label="City" htmlFor="stay-city" required>
          <Input
            id="stay-city"
            value={value.city}
            onChange={event => onChange({ ...value, city: event.target.value })}
          />
        </Field>
        <Field label={canada ? 'Province' : 'State'} htmlFor="stay-state" required>
          <Input
            id="stay-state"
            value={value.state}
            onChange={event => onChange({ ...value, state: event.target.value })}
          />
        </Field>
        <Field label={canada ? 'Postal code' : 'ZIP code'} htmlFor="stay-zip" required>
          <Input
            id="stay-zip"
            value={value.zipCode}
            onChange={event => onChange({ ...value, zipCode: event.target.value })}
          />
        </Field>
      </FormGrid>
      <Muted $size="sm" role={error ? 'alert' : undefined}>
        {error ??
          (located
            ? 'Map point set. Guests see an area within a few blocks until they book.'
            : 'Search above to set the map point.')}
      </Muted>
    </Stack>
  );
}
